// Readiness Scorecard v2 content + scoring config.
// The live config is admin-editable and fetched from GET /api/content/scorecard.
// This file holds the DEFAULT (fallback) config plus the pure scoring helpers.
// Routing (segment x band -> offer) stays in code. House style: no em dashes.

export const DEFAULT_SCORECARD = {
  intro: {
    headline: "Where are you running this from?",
    subhead:
      "The Readiness Scorecard is a free AI and technology readiness assessment. Answer ten questions, ten of the fifty we work through in a Sprint, and get a personalized map of what to build, buy, or fix next. A few minutes, no call required.",
    ten_of_fifty: "This is ten of the fifty questions we work through in a Sprint.",
  },
  // Q0, routing only, not scored. value is fixed; label is editable.
  segments: [
    { value: "founder", label: "Founder, building something" },
    { value: "enterprise", label: "Leader at an established company or enterprise" },
    { value: "nonprofit", label: "Mission-driven organization or nonprofit" },
    { value: "government", label: "Government or public-sector team" },
    { value: "advisor", label: "Advisor to one of the above" },
  ],
  // Ten questions, one per category, scored 0 to 3 (max 30).
  questions: [
    {
      key: "vision",
      category: "Vision & outcomes",
      tag: "Build",
      prompt:
        "How well are your current projects mapped to the top outcomes you must hit this year?",
      recommendation:
        "Map every active project to a named 12-month outcome before adding more.",
      options: [
        "Not mapped, we're reactive",
        "Loosely connected",
        "Mostly mapped",
        "Every project ties to a named outcome",
      ],
    },
    {
      key: "customer-fit",
      category: "Customer fit",
      tag: "Build",
      prompt:
        "Can you name your primary customer in one specific sentence, and show how you validate the solution is working?",
      recommendation:
        "Define the customer in one sentence and stand up real validation metrics.",
      options: [
        "Neither yet",
        "Can name them, can't validate",
        "Both, informally",
        "Specific customer, real validation metrics",
      ],
    },
    {
      key: "revenue",
      category: "Revenue & exposure",
      tag: "Fix",
      prompt:
        "Where are you most financially exposed, and how confident are you in your unit economics?",
      recommendation:
        "Reduce single-funder, client, or product concentration and pin down unit economics.",
      options: [
        "One funder, client, or product, shaky economics",
        "Concentrated, economics unclear",
        "Some diversification, economics mostly known",
        "Diversified, confident economics",
      ],
    },
    {
      key: "operations",
      category: "Operations",
      tag: "Fix",
      prompt:
        "Do your most critical recurring processes have documented SOPs, or do they live in people's heads?",
      recommendation:
        "Document critical processes into SOPs to remove key-person risk.",
      options: [
        "In people's heads",
        "A few documented",
        "Mostly documented, outdated",
        "Documented and current",
      ],
    },
    {
      key: "team",
      category: "Team & decision rights",
      tag: "Fix",
      prompt:
        "Are there clear owners for product, ops, tech, revenue, and impact, with clear decision rights?",
      recommendation:
        "Assign clear owners and decision rights across the five core functions.",
      options: [
        "Unclear, several single points of failure",
        "Some owners, contested decisions",
        "Mostly clear",
        "Clear owners and decision rights",
      ],
    },
    {
      key: "tech-data",
      category: "Technology & data",
      tag: "Fix / Buy",
      prompt:
        "How confidently can you answer, with data: who's most engaged, which channels convert, what drives impact or revenue?",
      recommendation:
        "Establish a single source of truth so you answer with data, not guesswork.",
      options: [
        "We can't",
        "With guesswork",
        "For some questions",
        "Confidently, from a source of truth",
      ],
    },
    {
      key: "impact",
      category: "Impact & measurement",
      tag: "Build / Fix",
      prompt:
        "Which impact or performance metrics do you actually track and review monthly, and can your data tell the story you need?",
      recommendation:
        "Stand up the monthly metrics that prove your impact to boards and funders.",
      options: [
        "No regular metrics",
        "Tracked, rarely reviewed",
        "Reviewed monthly, gaps in the story",
        "Reviewed monthly, story fully evidenced",
      ],
    },
    {
      key: "risk",
      category: "Risk & due-diligence readiness",
      tag: "Fix",
      prompt:
        "If an investor, regulator, or major funder requested a due-diligence or audit package tomorrow, how ready are you?",
      recommendation:
        "Assemble a due-diligence-ready operating record before someone asks for it.",
      options: [
        "We'd scramble, lots missing",
        "Weeks, significant gaps",
        "Days, minor gaps",
        "Ready now",
      ],
    },
    {
      key: "appetite",
      category: "Appetite for change",
      tag: "Signal",
      prompt:
        "What's the real appetite for change on your leadership team right now?",
      recommendation:
        "Names whether to start now or align leadership first.",
      options: [
        "Low, too much resistance",
        "Mixed",
        "Willing, stretched on capacity",
        "High and aligned",
      ],
    },
    {
      key: "priorities",
      category: "Priorities & next 90 days",
      tag: "Build",
      prompt:
        "Do you know your top three priorities and the quick wins for the next 90 days, or is it still unclear?",
      recommendation:
        "Turn the list into a prioritized 90-day plan with named quick wins.",
      options: [
        "Unclear",
        "A long list, no priorities",
        "Priorities, no plan",
        "Clear top three and quick wins",
      ],
    },
  ],
  // Scoring bands (max 30). key drives routing (ready/scaling = high).
  bands: [
    {
      key: "early",
      label: "Early",
      min: 0,
      max: 9,
      read:
        "Foundation, clarity, and ownership gaps. Priority: audit and a defined roadmap before building anything.",
    },
    {
      key: "forming",
      label: "Forming",
      min: 10,
      max: 18,
      read:
        "The pieces exist but aren't governed, documented, or prioritized. Priority: fix and document.",
    },
    {
      key: "ready",
      label: "Ready",
      min: 19,
      max: 25,
      read:
        "Solid foundation. Targeted gaps in data, measurement, readiness, or growth.",
    },
    {
      key: "scaling",
      label: "Scaling",
      min: 26,
      max: 30,
      read:
        "Strong. The work now is protecting independence, governance, and the next stage of scale.",
    },
  ],
};

// Q0.5, routing/personalization only, not scored. Asked right after segment.
export const SECTORS = [
  { value: "digital-health", label: "Digital health" },
  { value: "fintech", label: "Fintech" },
  { value: "enterprise-saas", label: "Enterprise SaaS / software" },
  { value: "consumer-app", label: "Consumer app / marketplace" },
  { value: "wellness", label: "Wellness / lifestyle" },
  { value: "nonprofit", label: "Nonprofit / mission-driven" },
  { value: "government", label: "Government / public sector" },
  { value: "other", label: "Other" },
];

// Two optional short-answer fields, asked after question 10. Optional but
// encouraged: they make the AI report read as written for the respondent.
export const SHORT_ANSWERS = [
  {
    key: "decision",
    label: "In one sentence, what's the most expensive decision you're carrying right now?",
    placeholder: "The decision that's costing you the most to get wrong...",
  },
  {
    key: "ready_definition",
    label: "What would \"ready\" look like for you?",
    placeholder: "When you'd know you were on solid ground...",
  },
];

// Find the band a score falls into (config-driven, with a safe fallback).
export function bandForScore(score, bands) {
  const list = bands && bands.length ? bands : DEFAULT_SCORECARD.bands;
  return list.find((b) => score >= b.min && score <= b.max) || list[0];
}

// Segment x band routing -> recommended offer. "high" = ready/scaling.
// Advisor always routes to the referral program. Routing stays in code.
const OFFER = {
  founderLow: { slug: "founder-tech-clarity-sprint", label: "Founder Tech Clarity Sprint" },
  founderHigh: { slug: "fractional-strategy-and-go-to-market", label: "Fractional Strategy & GTM" },
  enterprise: { slug: "enterprise-ai-readiness-governance-sprint", label: "Enterprise AI Readiness & Governance Sprint" },
  nonprofit: { slug: "nonprofit-tech-ai-readiness-sprint", label: "Nonprofit Tech & AI Readiness Sprint" },
  publicSector: { slug: "public-sector", label: "Public Sector brief" },
};

export function recommendedNextStep(segment, bandKey, appetiteScore) {
  const high = bandKey === "ready" || bandKey === "scaling";
  let offer;
  let referral = false;
  let publicSector = false;
  if (segment === "advisor") {
    referral = true;
  } else if (segment === "government") {
    offer = OFFER.publicSector;
    publicSector = true;
  } else if (segment === "enterprise") {
    offer = OFFER.enterprise;
  } else if (segment === "nonprofit") {
    offer = OFFER.nonprofit;
  } else {
    offer = high ? OFFER.founderHigh : OFFER.founderLow;
  }
  // Low appetite-for-change softens the routing: a conversation before a Sprint.
  const softened = !referral && (appetiteScore !== null && appetiteScore <= 1);
  return { offer, referral, softened, publicSector };
}

// Top three opportunities (the three lowest-scoring questions, ties broken by
// question order). Returns array of { ...question, score }.
export function lowestThree(answers, questions) {
  const qs = questions && questions.length ? questions : DEFAULT_SCORECARD.questions;
  return qs
    .map((q, i) => ({ ...q, order: i, score: answers[q.key] ?? 0 }))
    .sort((a, b) => a.score - b.score || a.order - b.order)
    .slice(0, 3);
}
